import { NOT_OBSERVED } from '@genoma/shared';

import { Brand } from '../../components/Brand.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { useSurveyStore } from '../../stores/survey.store.ts';

/**
 * Closing screen, shown in two situations: right after submitting, and when a
 * rater opens their link again later. Survey links stay valid, so returning
 * after finishing is a normal thing to do — it must read as confirmation, not
 * as a dead end.
 */
export const DoneScreen = () => {
  const { t } = useI18n();
  const survey = useSurveyStore((state) => state.survey);
  const completion = useSurveyStore((state) => state.completion);

  // `completion` only exists in the session that submitted. On a later visit
  // the same counts are recovered from the answers the API returns.
  const counts = completion ?? {
    answered: survey?.answers.filter((answer) => answer.value !== NOT_OBSERVED).length ?? 0,
    notObserved:
      survey?.answers.filter((answer) => answer.value === NOT_OBSERVED).length ?? 0,
  };

  const justFinished = completion !== null;
  const isSelf = survey?.kind === 'SELF';

  const firstName =
    survey?.evaluated.length === 1
      ? (survey.evaluated[0]?.fullName.split(' ')[0] ?? '')
      : '';

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-5">
      <Brand />

      <div className="flex flex-1 flex-col items-center justify-center py-14 text-center">
        <div className="mb-4 grid size-[62px] place-items-center rounded-full border-2 border-teal bg-tint text-[29px] text-teal">
          ✓
        </div>

        <h1 className="mb-2 text-[25px] font-extrabold tracking-[-0.02em]">
          {!justFinished
            ? t('survey.alreadyDoneTitle')
            : isSelf
              ? t('survey.doneTitleSelf')
              : t('survey.doneTitle')}
        </h1>

        <p className="mx-auto max-w-[38ch] text-[14.5px] text-soft">
          {t('survey.doneAnswered', { answered: counts.answered })}{' '}
          {counts.notObserved > 0 && t('survey.doneNotObserved', { count: counts.notObserved })}
        </p>

        {isSelf ? (
          <p className="mx-auto mt-1 max-w-[38ch] text-[14.5px] text-soft">
            {t('survey.doneTravellingSelf')}
          </p>
        ) : firstName ? (
          <p className="mx-auto mt-1 max-w-[38ch] text-[14.5px] text-soft">
            {t('survey.doneTravelling', { name: firstName })}
          </p>
        ) : (
          <p className="mx-auto mt-1 max-w-[38ch] text-[14.5px] text-soft">
            {t('survey.doneTravellingMany')}
          </p>
        )}

        {!justFinished && (
          <p className="mx-auto mt-5 max-w-[40ch] text-xs text-faint">
            {t('survey.alreadyDoneHelp')}
          </p>
        )}
      </div>
    </div>
  );
};
